import React from 'react';
import { Navigate } from 'react-router-dom';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { isV2PreviewEnabled } from '../utils/featureFlags';
import { GuardFallback } from './common/GuardFallback';

/**
 * V2 preview route guard (master plan Amendment, 2026-07-09).
 *
 * V2 surfaces render only when `isV2PreviewEnabled(user)` — dev builds,
 * or ADMIN/SUPER_ADMIN with VITE_V2_PREVIEW set. Everyone else bounces to
 * the classic portal, so brands cannot reach V2 even by typed URL.
 *
 * While the user is still resolving (prod only — dev short-circuits true),
 * render the guard fallback rather than redirecting prematurely: it spins,
 * surfaces session errors with a retry, and escalates a stalled spinner to
 * a reload panel instead of hanging forever.
 */
export const V2PreviewRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, loading, error, refetch } = useCurrentUser();

  if (isV2PreviewEnabled(user)) {
    return children;
  }

  if (error || loading) {
    return <GuardFallback error={error} onRetry={refetch} />;
  }

  return <Navigate to="/portal/total" replace />;
};
